import { ClipboardList, RefreshCw, Send } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { BrowseNavSidebar } from '@/components/shell/BrowseNavSidebar';
import { PageWithSidebar } from '@/components/shell/PageWithSidebar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BulkSendPanel } from '@/features/bulk-send/components/BulkSendPanel';

import { BrowseQuestionnairesTab } from './BrowseQuestionnairesPage';

type QuestionnairesTab = 'browse' | 'bulk-send';

/**
 * Questionnaires workspace (PTV2-1162): the "Browse" tab is the cross-patient questionnaire table
 * (default); the "Bulk Send" tab launches and reviews bulk email sends. Tabs mirror the Logs page.
 * A `?tab=bulk-send&job=<id>` deep link (from a patient's questionnaire) opens the Bulk Send tab
 * focused on that job.
 */
export function QuestionnairesPage(): ReactElement {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialJobId = searchParams.get('job') ?? undefined;
  const [tab, setTab] = useState<QuestionnairesTab>(() =>
    searchParams.get('tab') === 'bulk-send' ? 'bulk-send' : 'browse',
  );
  // "Keep updated" — when on, the Browse and Bulk Send tables poll so delivery/status changes appear
  // without a manual refresh.
  const [autoRefresh, setAutoRefresh] = useState(false);

  return (
    <PageWithSidebar sidebar={<BrowseNavSidebar />}>
      <div className="sticky top-0 z-20 border-b border-border bg-card px-8 py-3 flex items-center gap-3">
        <ClipboardList className="h-[18px] w-[18px] text-muted-foreground shrink-0" aria-hidden="true" />
        <h1 className="text-[15px] font-semibold tracking-tight text-foreground">
          {t('routes.questionnaires.heading')}
        </h1>
        <button
          type="button"
          role="switch"
          aria-checked={autoRefresh}
          onClick={() => {
            setAutoRefresh((on) => !on);
          }}
          className={`ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors ${
            autoRefresh
              ? 'border-primary/40 bg-primary/10 text-primary'
              : 'border-border text-muted-foreground hover:text-foreground'
          }`}
          title={t('routes.questionnaires.autoRefresh.hint')}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${autoRefresh ? 'animate-spin' : ''}`} aria-hidden="true" />
          {t('routes.questionnaires.autoRefresh.label')}
        </button>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v as QuestionnairesTab);
        }}
      >
        <TabsList aria-label={t('routes.questionnaires.heading')}>
          <TabsTrigger value="browse" icon={<ClipboardList className="h-4 w-4" aria-hidden="true" />}>
            {t('routes.questionnaires.tabs.browse')}
          </TabsTrigger>
          <TabsTrigger value="bulk-send" icon={<Send className="h-4 w-4" aria-hidden="true" />}>
            {t('routes.questionnaires.tabs.bulkSend')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="browse">
          <BrowseQuestionnairesTab autoRefresh={autoRefresh} />
        </TabsContent>
        <TabsContent value="bulk-send">
          <BulkSendPanel initialJobId={initialJobId} autoRefresh={autoRefresh} />
        </TabsContent>
      </Tabs>
    </PageWithSidebar>
  );
}
